/**
 * Armada Downhill: the way in, and the loader that goes with it.
 *
 * The game is not on this page. Nothing of it is: no markup, no stylesheet, no
 * engine. This file is the entire cost the storefront pays for the easter egg,
 * and it does two things.
 *
 * 1. Watches the header logo for three quick clicks. A single click still goes
 *    home, it just waits `HOLD` milliseconds first to see whether a second and
 *    third are coming: short enough to read as instant, long enough for a
 *    deliberate triple click. Keyboard activation, modified clicks and middle
 *    clicks are left alone, so "open in a new tab" and Enter on the focused
 *    logo keep working.
 *
 * 2. On the third click, opens a full-screen overlay that paints immediately -
 *    its chrome and its loading animation are the handful of inlined rules
 *    below, so there is something on screen before a single byte of the game is
 *    requested: then pulls the markup, the two stylesheets and the engine in
 *    parallel and swaps them in when all four have landed. The bar underneath
 *    the skier is real progress, not a timer: one cell per resource resolved.
 *
 * Everything is fetched once. Closing the overlay detaches the game rather than
 * destroying it, so a second visit is instant and the engine keeps its state.
 */
(() => {
  'use strict';

  const CLICKS = 3;
  const GAP = 500; /* ms allowed between clicks of the sequence */
  const HOLD = 220; /* ms the logo waits before following its own link */

  /* The shareable way in: any page on the store plus `?lilenzo=downhill`.

     It is a trigger and not a state, so it comes out of the address bar the
     moment it has done its job. Left there it would read as somewhere the
     visitor had navigated to: surviving a close, returning on reload, and
     riding along in any link they copied afterwards.

     A parameter rather than a page, because a parameter is not a link: there is
     still nothing on the site for a crawler to follow. */
  const PARAM = 'lilenzo';
  const PARAM_VALUE = 'downhill';

  /* Long enough that the loader reads as a deliberate animation rather than a
     flash of chrome, short enough that nobody waits on it. On a warm cache the
     four resources beat this every time, and this is what you actually watch. */
  const FLOOR = 700;

  /* Every string the launcher paints comes from Liquid, so the loading screen
     and the close key are translated with the rest of the game. The English in
     the fallbacks is only for the case where the attributes are absent. */
  const script = document.currentScript;
  const config = script?.dataset ?? {};

  if (!config.fragmentUrl || !config.gameJs) return;

  const logo = document.querySelector('.header__heading-link');

  /* Overlay chrome and loader ---------------------------------------------- */

  /* The close key is framed the way every button in the game is framed: four
     offset shadows rather than a border, which leaves the four corners open -
     the chip that makes an eight-bit box read as an eight-bit box.

     The ring the lit pair walks therefore skips those corners too. Lighting a
     pixel in a corner would fill the very gap the frame exists to leave, and
     the chip would blink out four times a lap. That makes each lap 2 x (W + H)
     pixels, so 11 by 5 is what lands on exactly 32.

     Walking a flat list of cells rather than four edges is what lets the pair
     turn a corner without a special case: the trailing pixel is simply the
     previous cell, which across a chip is the diagonal neighbour. */
  const FRAME_W = 11;
  const FRAME_H = 5;

  const frameCells = () => {
    const cells = [];

    for (let x = 0; x < FRAME_W; x += 1) cells.push([x, -1]);
    for (let y = 0; y < FRAME_H; y += 1) cells.push([FRAME_W, y]);
    for (let x = FRAME_W - 1; x >= 0; x -= 1) cells.push([x, FRAME_H]);
    for (let y = FRAME_H - 1; y >= 0; y -= 1) cells.push([-1, y]);

    return cells;
  };

  const cellPx = (n) => (n === 0 ? '0px' : `calc(var(--adh-px) * ${n})`);

  const frameKeyframes = () => {
    const cells = frameCells();

    return cells
      .map(([x, y], index) => {
        const [prevX, prevY] = cells[(index - 1 + cells.length) % cells.length];

        return `  ${((index / cells.length) * 100).toFixed(4)}% { left: ${cellPx(x)}; top: ${cellPx(
          y
        )}; box-shadow: ${cellPx(prevX - x)} ${cellPx(prevY - y)} 0 0 #8b2fa0; }`;
      })
      .join('\n');
  };

  const CSS = `
.adh {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  --adh-px: 3px;
}
/* The original pixel key, which was the right shape all along: it was only
   ever white on white snow. Same frame, same corner, ARV 94 colours. The label
   sits still in aurora green; two adjacent pixels of the frame light up purple
   and walk around it one pixel at a time, 32 stops to the lap, skipping the
   chipped corners.

   Sized in whole cells with no padding, so it takes a corner of the slope
   rather than a bite out of it. */
.adh__close {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: calc(var(--adh-px) * ${FRAME_W});
  height: calc(var(--adh-px) * ${FRAME_H});
  margin: calc(var(--adh-px) * 3);
  padding: 0;
  appearance: none;
  border: 0;
  border-radius: 0;
  background: transparent;
  /* Four offset shadows, exactly as the Send it key is built, so the corners
     stay chipped instead of squaring off. */
  box-shadow: 0 calc(-1 * var(--adh-px)) 0 0 #93e024, 0 var(--adh-px) 0 0 #93e024,
    calc(-1 * var(--adh-px)) 0 0 0 #93e024, var(--adh-px) 0 0 0 #93e024;
  color: #93e024;
  cursor: pointer;
  font: inherit;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
/* The lit pair: this pixel, plus the one behind it carried as a shadow. */
.adh__close::before {
  content: '';
  position: absolute;
  width: var(--adh-px);
  height: var(--adh-px);
  background: #8b2fa0;
  animation: adh-frame 2.4s steps(1) infinite;
}
/* The key is deliberately small; the target it answers to is not. */
.adh__close::after {
  content: '';
  position: absolute;
  inset: calc(var(--adh-px) * -4);
}
@keyframes adh-frame {
${frameKeyframes()}
}
.adh__close:active { transform: translateY(var(--adh-px)); }
.adh__close:focus-visible { outline: var(--adh-px) solid #ededed; outline-offset: calc(var(--adh-px) * 2); }
.adh__game { width: 100%; height: 100%; display: flex; }
.adh__game > * { flex: 1 1 auto; min-height: 0; }

/* Loader ------------------------------------------------------------------ */
.adh__loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: calc(var(--adh-px) * 8);
  color: #fff;
  text-align: center;
  padding: calc(var(--adh-px) * 6);
}
.adh__title {
  margin: 0;
  font-size: 16px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
}
.adh__title::after {
  content: '';
  animation: adh-dots 1.2s steps(4) infinite;
}
@keyframes adh-dots {
  0% { content: ''; }
  25% { content: '.'; }
  50% { content: '..'; }
  75% { content: '...'; }
}
.adh__track { position: relative; display: flex; gap: var(--adh-px); }
.adh__cell {
  width: calc(var(--adh-px) * 4);
  height: calc(var(--adh-px) * 6);
  background: #1e1e1e;
  box-shadow: inset 0 0 0 var(--adh-px) #2e2e2e;
  transition: background-color 0.18s steps(2);
}
.adh__cell[data-on] { background: #fff; box-shadow: none; }

/* The rider is drawn in box-shadow pixels off a single 1px block, on the same
   grid as the game it is loading, and rides the bar as the cells fill. */
.adh__rider {
  position: absolute;
  bottom: calc(100% + var(--adh-px) * 2);
  left: 0;
  width: var(--adh-px);
  height: var(--adh-px);
  background: transparent;
  transition: transform 0.25s steps(3);
  color: #fff;
  box-shadow:
    calc(var(--adh-px) * 2) 0 0 #ff2e88,
    calc(var(--adh-px) * 3) 0 0 #ff2e88,
    calc(var(--adh-px) * 1) var(--adh-px) 0 #ff2e88,
    calc(var(--adh-px) * 2) var(--adh-px) 0 #fff,
    calc(var(--adh-px) * 3) var(--adh-px) 0 #fff,
    calc(var(--adh-px) * 1) calc(var(--adh-px) * 2) 0 #8a2be2,
    calc(var(--adh-px) * 2) calc(var(--adh-px) * 2) 0 #8a2be2,
    calc(var(--adh-px) * 3) calc(var(--adh-px) * 2) 0 #8a2be2,
    calc(var(--adh-px) * 1) calc(var(--adh-px) * 3) 0 #00b3a4,
    calc(var(--adh-px) * 2) calc(var(--adh-px) * 3) 0 #00b3a4,
    calc(var(--adh-px) * 3) calc(var(--adh-px) * 3) 0 #00b3a4,
    0 calc(var(--adh-px) * 4) 0 #9aec0e,
    var(--adh-px) calc(var(--adh-px) * 4) 0 #9aec0e,
    calc(var(--adh-px) * 2) calc(var(--adh-px) * 4) 0 #9aec0e,
    calc(var(--adh-px) * 3) calc(var(--adh-px) * 4) 0 #9aec0e,
    calc(var(--adh-px) * 4) calc(var(--adh-px) * 4) 0 #9aec0e;
  animation: adh-bob 0.4s steps(2) infinite;
}
@keyframes adh-bob {
  50% { margin-bottom: var(--adh-px); }
}

/* Three pixels of weather, so the black is not just black. */
.adh__snow, .adh__snow::before, .adh__snow::after {
  position: absolute;
  top: -10vh;
  width: var(--adh-px);
  height: var(--adh-px);
  background: #fff;
  animation: adh-fall 3.2s linear infinite;
}
.adh__snow { left: 22%; }
.adh__snow::before { content: ''; left: 160px; animation-delay: -1.1s; opacity: 0.6; }
.adh__snow::after { content: ''; left: 380px; animation-delay: -2.3s; opacity: 0.35; }
@keyframes adh-fall {
  to { transform: translateY(120vh); }
}

.adh__error { margin: 0; font-size: 14px; letter-spacing: 0.08em; text-transform: uppercase; }

@media (prefers-reduced-motion: reduce) {
  .adh__title::after, .adh__rider, .adh__snow,
  .adh__snow::before, .adh__snow::after, .adh__close::before { animation: none; }
  .adh__title::after { content: '...'; }
  .adh__cell, .adh__rider { transition: none; }
}
`;

  const CELLS = 18;

  let styleInjected = false;
  let overlay = null; /* the <div class="adh"> once built */
  let gameNode = null; /* the fetched game, kept detached between visits */
  let loading = null; /* in-flight load promise, so a double open shares one */
  let lastFocus = null;

  const injectStyle = () => {
    if (styleInjected) return;

    const style = document.createElement('style');

    style.textContent = CSS;
    document.head.appendChild(style);
    styleInjected = true;
  };

  /* Resource loading -------------------------------------------------------- */

  const loadStylesheet = (href) =>
    new Promise((resolve, reject) => {
      if (document.querySelector(`link[href="${href}"]`)) {
        resolve();
        return;
      }

      const link = document.createElement('link');

      link.rel = 'stylesheet';
      link.href = href;
      link.onload = resolve;
      link.onerror = () => reject(new Error(href));
      document.head.appendChild(link);
    });

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const tag = document.createElement('script');

      tag.src = src;
      tag.onload = resolve;
      tag.onerror = () => reject(new Error(src));
      document.head.appendChild(tag);
    });

  const loadFragment = async (url) => {
    const response = await fetch(url, { credentials: 'same-origin' });

    if (!response.ok) throw new Error(`${response.status} ${url}`);

    const markup = await response.text();
    const holder = document.createElement('div');

    holder.innerHTML = markup;

    const root = holder.querySelector('[data-ski-game]');

    if (!root) throw new Error('no game in fragment');

    return root;
  };

  /* Overlay ----------------------------------------------------------------- */

  /* replaceState rather than pushState: the visitor never navigated anywhere,
     so the back button should take them off the page they arrived on rather
     than stepping back through a parameter being removed. */
  const clearTrigger = () => {
    if (!window.history || !window.history.replaceState) return;

    try {
      const url = new URL(window.location.href);

      if (!url.searchParams.has(PARAM)) return;

      url.searchParams.delete(PARAM);
      window.history.replaceState(window.history.state, '', url);
    } catch (error) {
      /* Sandboxed or cross-origin contexts refuse this. The game still opens. */
    }
  };

  const buildOverlay = () => {
    const el = document.createElement('div');

    el.className = 'adh';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Armada Downhill');
    el.innerHTML = `
      <button type="button" class="adh__close" data-adh-close aria-label="${config.closeLabel || 'Close the game'}">Esc</button>
      <div class="adh__loader" data-adh-loader>
        <span class="adh__snow" aria-hidden="true"></span>
        <p class="adh__title">${config.loadingText || 'Loading the slope'}</p>
        <div class="adh__track" data-adh-track>
          <span class="adh__rider" data-adh-rider aria-hidden="true"></span>
          ${'<span class="adh__cell"></span>'.repeat(CELLS)}
        </div>
      </div>
      <div class="adh__game" data-adh-game hidden></div>
    `;

    el.addEventListener('click', (event) => {
      if (event.target.closest('[data-adh-close]')) close();
    });

    return el;
  };

  const setProgress = (done, total) => {
    if (!overlay) return;

    const filled = Math.round((done / total) * CELLS);
    const cells = overlay.querySelectorAll('.adh__cell');

    cells.forEach((cell, index) => {
      if (index < filled) cell.setAttribute('data-on', '');
      else cell.removeAttribute('data-on');
    });

    const rider = overlay.querySelector('[data-adh-rider]');
    const cell = cells[0];

    if (rider && cell) {
      /* One cell plus its gap, in the same units the track is laid out in. */
      const step = cell.getBoundingClientRect().width + 3;

      rider.style.transform = `translateX(${Math.max(0, filled - 1) * step}px)`;
    }
  };

  const showError = () => {
    const loader = overlay?.querySelector('[data-adh-loader]');

    if (!loader) return;

    loader.innerHTML = `<p class="adh__error">${config.errorText || 'The lift is closed. Try again later.'}</p>`;
  };

  /**
   * Fetches everything once and resolves with the game element.
   *
   * The four requests go out together: the markup does not gate the engine,
   * because the engine is told to initialise explicitly rather than scanning
   * the document when it loads.
   */
  const load = () => {
    if (loading) return loading;

    const jobs = [
      loadFragment(config.fragmentUrl),
      loadStylesheet(config.gameCss),
      loadScript(config.gameJs),
    ];

    if (config.buttonCss) jobs.push(loadStylesheet(config.buttonCss));

    const total = jobs.length;
    let done = 0;

    setProgress(0, total);

    jobs.forEach((job) => {
      job.then(() => {
        done += 1;
        setProgress(done, total);
      }).catch(() => {});
    });

    loading = Promise.all([...jobs, new Promise((resolve) => window.setTimeout(resolve, FLOOR))])
      .then(([root]) => root)
      .catch((error) => {
        /* Let the next attempt start clean rather than caching the failure. */
        loading = null;
        throw error;
      });

    return loading;
  };

  const onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  };

  function close() {
    if (!overlay) return;

    const holder = overlay.querySelector('[data-adh-game]');

    /* Detached, not destroyed: reopening is instant and the run survives. The
       loop does not, though: an unwatched slope should cost nothing. */
    if (gameNode && holder?.contains(gameNode)) {
      window.ArmadaSkiGame?.pause?.(gameNode);
      gameNode.remove();
    }

    overlay.remove();
    overlay = null;

    document.removeEventListener('keydown', onKeydown, true);
    document.documentElement.style.overflow = '';
    lastFocus?.focus?.();
  }

  async function open() {
    if (overlay) return;

    injectStyle();

    lastFocus = document.activeElement;
    overlay = buildOverlay();
    document.body.appendChild(overlay);
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeydown, true);
    overlay.querySelector('[data-adh-close]')?.focus();

    let root;

    try {
      root = gameNode ?? (await load());
    } catch (error) {
      showError();
      return;
    }

    /* Closed again while the slope was still loading. */
    if (!overlay) return;

    gameNode = root;

    const holder = overlay.querySelector('[data-adh-game]');
    const loader = overlay.querySelector('[data-adh-loader]');

    holder.appendChild(gameNode);
    holder.hidden = false;
    loader?.remove();

    /* First visit builds the instance; later ones only have to re-measure, since
       the game sat detached through every resize that happened meanwhile. */
    window.ArmadaSkiGame?.init?.(gameNode);
    window.ArmadaSkiGame?.refresh?.(gameNode);
    gameNode.focus?.();
  }

  /* The way in -------------------------------------------------------------- */

  let count = 0;
  let holdTimer = null;
  let sequenceTimer = null;

  const reset = () => {
    count = 0;
    window.clearTimeout(sequenceTimer);
  };

  logo?.addEventListener('click', (event) => {
    /* detail 0 means the link was activated from the keyboard. */
    if (event.detail === 0 || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();

    count += 1;
    window.clearTimeout(holdTimer);
    window.clearTimeout(sequenceTimer);

    if (count >= CLICKS) {
      reset();
      open();
      return;
    }

    sequenceTimer = window.setTimeout(reset, GAP);
    holdTimer = window.setTimeout(() => {
      reset();
      window.location.href = logo.href;
    }, HOLD);
  });

  /* Arriving on a shared link. Checked after the logo is wired rather than
     before, so a page with no header still opens the game, and the parameter is
     spent the instant it is read. */
  try {
    if (new URLSearchParams(window.location.search).get(PARAM) === PARAM_VALUE) {
      clearTrigger();
      open();
    }
  } catch (error) {
    /* An unparseable query string is not a reason to break the page. */
  }
})();
